import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Loader2,
  CheckCircle,
  AlertCircle,
  Download,
  Save,
  X,
  Mail,
  FilePlus,
  Pencil,
  Check,
  LayoutTemplate,
  Square,
  RotateCcw,
  RefreshCw,
  MessageSquare,
  FileCheck,
  Languages,
  Layout,
  Palette,
  OctagonAlert,
  FileSignature,
  Clock,
  PenTool,
  Send,
} from "lucide-react";

import { SigningWizardModal } from "@/components/shared/SigningWizardModal";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { useSmoothText, type UIMessage } from "@convex-dev/agent/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import type { Doc } from "@/convex/_generated/dataModel";
import { useTypstCompiler } from "@/hooks/useTypstCompiler";
import { useSyntaxValidator } from "@/hooks/useSyntaxValidator";
import { PDFViewer } from "./PDFViewer";
import { useAction, useMutation, useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { useAuth } from "@clerk/clerk-react";
import { EmailComposerSheet } from "./EmailComposerSheet";
import { SaveTemplateDialog } from "./SaveTemplateDialog";
import { UpgradePromptDialog } from "@/components/billing/UpgradePromptDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Bars Loader Component - Animated vertical bars loading indicator
const BarsLoader = ({ size = 24, className }: { size?: number; className?: string }) => (
  <svg
    height={size}
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <title>Loading...</title>
    <style>{`
      .spinner-bar {
        animation: spinner-bars-animation .8s linear infinite;
        animation-delay: -.8s;
      }
      .spinner-bars-2 {
        animation-delay: -.65s;
      }
      .spinner-bars-3 {
        animation-delay: -0.5s;
      }
      @keyframes spinner-bars-animation {
        0% {
          y: 1px;
          height: 22px;
        }
        93.75% {
          y: 5px;
          height: 14px;
          opacity: 0.2;
        }
      }
    `}</style>
    <rect className="spinner-bar" fill="currentColor" height="22" width="6" x="1" y="1" />
    <rect
      className="spinner-bar spinner-bars-2"
      fill="currentColor"
      height="22"
      width="6"
      x="9"
      y="1"
    />
    <rect
      className="spinner-bar spinner-bars-3"
      fill="currentColor"
      height="22"
      width="6"
      x="17"
      y="1"
    />
  </svg>
);

interface DocumentCanvasProps {
  message: UIMessage | undefined;
  threadDocument?: Doc<"documents"> | null; // The actual document from the database
  onRendered?: () => void;

  className?: string;
  onRefineRequest?: () => void; // Callback to open sidebar for refinement
  onRetryRequest?: () => void; // Callback to restart generation completely
  onPdfReady?: (isReady: boolean) => void; // Callback when PDF compilation completes
  workflowId?: string; // Workflow ID for cancellation
  threadId?: string; // Thread ID for cancellation
}

/**
 * DocumentCanvas component displays the generated document with streaming support
 * Follows the official Convex agent pattern using useSmoothText hook
 *
 * Priority for displaying content:
 * 1. During streaming: Use useSmoothText for character-by-character typewriter effect
 * 2. After streaming: Use database content (threadDocument) if available, otherwise message text
 *
 * Note: This component only supports Typst documents. Markdown support has been removed.
 */
export function DocumentCanvas({
  message,
  threadDocument,
  className,
  onRefineRequest,
  onRetryRequest,
  onPdfReady,
  workflowId,
  threadId,
}: DocumentCanvasProps) {
  const navigate = useNavigate();
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isStoppedManually, setIsStoppedManually] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const { toast } = useToast();
  const { userId } = useAuth();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState("");
  
  // Fetch bid data if this document is attached to a bid
  const bid = useQuery(api.bids.getBid, threadDocument?.bidId ? { id: threadDocument.bidId as any } : "skip");

  const initiateSigningMutation = useMutation(api.documents.initiateSigningWorkflow);
  const submitSignatureMutation = useMutation(api.documents.submitSignature);


  // Use database attempt count instead of client state
  const attemptCount = threadDocument?.attemptCount || 0;
  // Debounce vars: tracks when compiler error first appeared, delays the failed UI
  const compilerErrorSinceRef = useRef<number | null>(null);
  const [showCompilerErrorUI, setShowCompilerErrorUI] = useState(false);
  const compilerErrorUiTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Debounce resetting parent isPdfReady during auto-repair
  const pdfNotReadyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize Typst compiler
  const {
    isReady: isCompilerReady,
    compile,
    isCompiling,
    error: compilerError,
    clearError,
  } = useTypstCompiler();

  // Mutation to cancel document generation
  const cancelGeneration = useMutation(api.documentWriterStreaming.cancelDocumentGeneration);

  // Convex action for finalizing documents
  const finalizeDocument = useAction(api.documentFinalizer.finalizeDocument);

  // Convex Mutations
  const setPublic = useMutation(api.documents.setDocumentPublic);
  const refineDocument = useMutation(api.documentWriterStreaming.refineDocument);
  const updateTitle = useMutation(api.documents.updateDocumentTitle);
  const updateContent = useMutation(api.documents.updateDocumentContent);

  // Query subscription for free tier check
  const subscriptionData = useQuery(api.subscriptions.getOrganizationSubscription);

  // Determine document type from database document
  // Default to 'typst' to align with agent's default output type
  const outputType = threadDocument?.outputType || "typst";
  const isTypstDocument = outputType === "typst";

  // Unified Compiler Service: Compute direct server PDF URL if compiledPdfStorageId exists in Convex
  const convexSiteUrl = (import.meta.env.VITE_CONVEX_URL || "https://hardy-lemur-357.convex.cloud").replace(".cloud", ".site");
  const serverPdfUrl = threadDocument?.compiledPdfStorageId
    ? `${convexSiteUrl}/getDocument?id=${threadDocument.compiledPdfStorageId}&title=${encodeURIComponent(threadDocument.title || "Document")}`
    : null;

  // Check if document is finalized (e.g. ready for signature, pending, or signed)
  const isFinalized = threadDocument?.status === "finalized_ready_for_signature" || 
                      threadDocument?.status === "signing_pending" || 
                      threadDocument?.status === "signed";

  // Effect to clear compiler error when starting a new generation
  useEffect(() => {
    if (threadId) {
      console.log("[DocumentCanvas] New threadId detected, clearing compiler errors:", threadId);
      clearError();
      setShowCompilerErrorUI(false); // Also reset the UI debounce
      setIsStoppedManually(false);
    }
  }, [threadId, clearError]);

  // DEBUG: Log document type detection
  useEffect(() => {
    console.log("[DocumentCanvas] Document type detection:", {
      hasThreadDocument: !!threadDocument,
      documentId: threadDocument?._id,
      outputType,
      isTypstDocument,
      hasTypstContent: !!threadDocument?.typstContent,
      typstContentLength: threadDocument?.typstContent?.length,
    });
  }, [threadDocument, outputType, isTypstDocument]);

  // Determine streaming state based on message status
  const isStreaming = message?.status === "streaming";

  // DEBUG: Log streaming state
  useEffect(() => {
    console.log("[DocumentCanvas] Streaming state:", {
      hasMessage: !!message,
      messageStatus: message?.status,
      isStreaming,
      messageText: message?.text?.substring(0, 100),
    });
  }, [message, isStreaming]);

  // Debug logging for document type detection
  useEffect(() => {
    console.log("[DocumentCanvas] Document type detection:", {
      hasThreadDocument: !!threadDocument,
      outputType,
      isTypstDocument,
      hasTypstContent: !!threadDocument?.typstContent,
      typstContentLength: threadDocument?.typstContent?.length || 0,
      isCompilerReady,
      isStreaming,
    });
  }, [threadDocument, outputType, isTypstDocument, isCompilerReady, isStreaming]);

  // ✅ OFFICIAL PATTERN: Use useSmoothText hook for typewriter effect during streaming
  // Pass message.text directly to the hook, which handles streaming automatically
  const [visibleText] = useSmoothText(message?.text || "", {
    startStreaming: isStreaming,
  });

  // Initialize Syntax Validator for Proactive Syntax Engine
  // Stream interruption is now disabled in favor of agent-driven compilation and correction
  const { validate, isReady: isValidatorReady } = useSyntaxValidator({
    enabled: true,
    debounceMs: 250,
    // onCriticalError is intentionally omitted
  });

  // Effect to validate content during streaming
  useEffect(() => {
    if (isStreaming && visibleText && isValidatorReady) {
      validate(visibleText);
    }
  }, [isStreaming, visibleText, isValidatorReady, validate]);

  // Final content to display:
  // - For Typst documents: Use typstContent field, but fallback to lastKnownGoodContent if current fails
  // - For Markdown documents: Use content field
  // - During streaming: Use visibleText from useSmoothText for typewriter effect
  // - After streaming: Use database content (most up-to-date)
  //
  // NOTE: The agent sends a SHORT conversational message (e.g., "I've generated your proposal...")
  // The actual document content is saved to the documents table, NOT in the message
  // So we prioritize threadDocument content over message.text

  // Use LKGS if compilation failed and we're not streaming a new version
  const useLKGS =
    isTypstDocument && !!compilerError && threadDocument?.lastKnownGoodContent && !isStreaming;

  const content = isStreaming
    ? visibleText
    : isTypstDocument
      ? useLKGS
        ? threadDocument.lastKnownGoodContent
        : threadDocument?.typstContent || ""
      : threadDocument?.content || "";

  // DEBUG: Log content determination
  useEffect(() => {
    console.log("[DocumentCanvas] Content determination:", {
      isStreaming,
      isTypstDocument,
      hasContent: !!content,
      contentLength: content?.length,
      contentSource: isStreaming
        ? "visibleText"
        : useLKGS
          ? "threadDocument.lastKnownGoodContent"
          : isTypstDocument
            ? "threadDocument.typstContent"
            : "threadDocument.content",
      contentPreview: content?.substring(0, 100),
    });
  }, [content, isStreaming, isTypstDocument, useLKGS]);

  const lastRepairAttemptRef = useRef<number>(0);

  // Reset auto repair when successful PDF compile occurs
  useEffect(() => {
    if (pdfBlob) {
      lastRepairAttemptRef.current = 0;
      // Clear compiler error debounce — WASM compile succeeded
      compilerErrorSinceRef.current = null;
      setShowCompilerErrorUI(false);
      if (compilerErrorUiTimerRef.current) {
        clearTimeout(compilerErrorUiTimerRef.current);
        compilerErrorUiTimerRef.current = null;
      }
      // Cancel any pending "not ready" signal — we ARE ready now
      if (pdfNotReadyTimerRef.current) {
        clearTimeout(pdfNotReadyTimerRef.current);
        pdfNotReadyTimerRef.current = null;
      }
    }
  }, [pdfBlob]);

  // Debounce the compiler error UI: transient errors (auto-repair in flight) should
  // not immediately show the failed state — only show it after 20 s of continuous error.
  useEffect(() => {
    if (compilerError && !serverPdfUrl) {
      if (compilerErrorSinceRef.current === null) {
        compilerErrorSinceRef.current = Date.now();
      }
      if (!compilerErrorUiTimerRef.current) {
        compilerErrorUiTimerRef.current = setTimeout(() => {
          compilerErrorUiTimerRef.current = null;
          setShowCompilerErrorUI(true);
        }, 20_000); // 20 s grace — auto-repair usually finishes in < 10 s
      }
    } else {
      compilerErrorSinceRef.current = null;
      setShowCompilerErrorUI(false);
      if (compilerErrorUiTimerRef.current) {
        clearTimeout(compilerErrorUiTimerRef.current);
        compilerErrorUiTimerRef.current = null;
      }
    }
  }, [compilerError, serverPdfUrl]);

  // Clean up all debounce timers on unmount
  useEffect(() => {
    return () => {
      if (pdfNotReadyTimerRef.current) clearTimeout(pdfNotReadyTimerRef.current);
      if (compilerErrorUiTimerRef.current) clearTimeout(compilerErrorUiTimerRef.current);
    };
  }, []);

  // Compile Typst content to PDF when it changes
  useEffect(() => {
    console.log("[DocumentCanvas] Compilation effect triggered");

    // Only compile if this is a Typst document
    if (!isTypstDocument) {
      console.log("[DocumentCanvas] Not a Typst document, skipping compilation");
      setPdfBlob(null);
      return;
    }

    // Only compile if compiler is ready
    if (!isCompilerReady) {
      console.log("[DocumentCanvas] Compiler not ready yet, waiting...");
      return;
    }

    // Only compile if we have Typst content
    const typstContent = content || threadDocument?.typstContent;
    if (!typstContent || typstContent.trim().length === 0) {
      console.log("[DocumentCanvas] No Typst content to compile, clearing PDF and errors");
      setPdfBlob(null);
      clearError();
      return;
    }

    // Don't compile during streaming to avoid excessive compilations
    if (isStreaming) {
      console.log("[DocumentCanvas] Still streaming, waiting for completion...");
      return;
    }

    // Unified Service: If server already compiled the PDF to Convex Storage, skip client WASM compilation
    if (serverPdfUrl) {
      console.log("[DocumentCanvas] Server PDF available in Convex Storage, skipping local WASM compile:", serverPdfUrl);
      clearError();
      if (pdfNotReadyTimerRef.current) {
        clearTimeout(pdfNotReadyTimerRef.current);
        pdfNotReadyTimerRef.current = null;
      }
      return;
    }

    console.log("[DocumentCanvas] Starting Typst compilation...", {
      contentLength: typstContent.length,
      contentPreview: typstContent.substring(0, 100),
      isUsingLKGS: useLKGS,
    });

    // Debounce signalling "not ready" to the parent: only fire after 20 s so that
    // transient WASM errors resolved by auto-repair (~8 s) don't regress the parent state.
    if (!pdfNotReadyTimerRef.current) {
      pdfNotReadyTimerRef.current = setTimeout(() => {
        pdfNotReadyTimerRef.current = null;
        onPdfReady?.(false);
      }, 20_000);
    }

    // Compile Typst to PDF
    const compileDocument = async () => {
      try {
        const blob = await compile(typstContent);
        if (blob) {
          console.log("[DocumentCanvas] Compilation successful! PDF blob size:", blob.size);
          setPdfBlob(blob);
        } else {
          console.error("[DocumentCanvas] Compilation returned null blob");
        }
      } catch (error) {
        console.error("[DocumentCanvas] Error compiling Typst document:", error);
      }
    };

    compileDocument();
  }, [
    isTypstDocument,
    isCompilerReady,
    content,
    threadDocument?.typstContent,
    isStreaming,
    useLKGS,
    compile,
    onPdfReady,
  ]);

  // Notify parent when PDF is ready or compilation state changes
  // For Typst documents: we wait for PDFViewer's onRendered callback (see handlePdfRendered below)
  // For non-Typst documents: ready when we have content
  useEffect(() => {
    if (!isTypstDocument) {
      const isReady = !!content;
      onPdfReady?.(isReady);
    }
  }, [isTypstDocument, content, onPdfReady]);

  // Called by PDFViewer once the PDF.js Document has parsed the file and is rendering pages
  const handlePdfRendered = () => {
    console.log("[DocumentCanvas] PDF rendered in viewer, signaling ready to parent");
    onPdfReady?.(true);
  };

  // Export handlers
  const handleDownload = () => {
    // For Typst documents, download PDF if available
    if (isTypstDocument && (pdfBlob || serverPdfUrl)) {
      try {
        // Use document title if available, otherwise use a default name
        const filename = threadDocument?.title
          ? `${threadDocument.title}.pdf`
          : `document-${new Date().toISOString().split("T")[0]}.pdf`;

        if (pdfBlob) {
          const url = URL.createObjectURL(pdfBlob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } else if (serverPdfUrl) {
          const a = document.createElement("a");
          a.href = serverPdfUrl;
          a.download = filename;
          a.target = "_blank";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }

        toast({
          title: "Download started",
          description: "Your PDF is being downloaded.",
          duration: 5000,
        });
      } catch (error) {
        console.error("Failed to download PDF:", error);
        toast({
          title: "Download failed",
          description: "Failed to download PDF. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
      }
      return;
    }

    // For non-Typst documents, download Markdown
    if (!content) return;

    try {
      const blob = new Blob([content], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const filename = threadDocument?.title
        ? `${threadDocument.title}.md`
        : `document-${new Date().toISOString().split("T")[0]}.md`;

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Download started",
        description: "Your document is being downloaded as a Markdown file.",
        duration: 5000,
      });
    } catch (error) {
      console.error("Failed to download document:", error);
      toast({
        title: "Download failed",
        description: "Failed to download document. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleOpenEmailComposer = async () => {
    if (pdfBlob) {
      setEmailComposerOpen(true);
      return;
    }
    if (serverPdfUrl) {
      try {
        const res = await fetch(serverPdfUrl);
        const blob = await res.blob();
        setPdfBlob(blob);
        setEmailComposerOpen(true);
      } catch (e) {
        console.error("Failed to load PDF for email composer:", e);
      }
    }
  };

  const handleFinalize = async () => {
    if (!threadDocument?._id || (!pdfBlob && !serverPdfUrl)) {
      toast({
        title: "Cannot save PDF",
        description: "Document must be compiled to PDF before saving.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsFinalizing(true);
      console.log("[DocumentCanvas] Starting PDF save for document:", threadDocument._id);

      let base64: string;
      if (pdfBlob) {
        const base64DataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(pdfBlob);
        });
        base64 = base64DataUrl.split(',')[1];
      } else if (serverPdfUrl) {
        const res = await fetch(serverPdfUrl);
        const blob = await res.blob();
        const base64DataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        base64 = base64DataUrl.split(',')[1];
      } else {
        return;
      }

      // Call the finalization action
      const result = await finalizeDocument({
        documentId: threadDocument._id,
        pdfBlob: base64,
      });

      console.log("[DocumentCanvas] PDF save result:", result);

      const isUpdate = threadDocument.status === "finalized_ready_for_signature";

      toast({
        title: isUpdate ? "PDF Updated" : "PDF Saved",
        description: isUpdate
          ? "The PDF has been updated with your latest changes."
          : "Your document has been saved to your account and is ready to download.",
      });
    } catch (error) {
      console.error("[DocumentCanvas] PDF save error:", error);
      toast({
        title: "Save Failed",
        description:
          error instanceof Error ? error.message : "Failed to save PDF. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsFinalizing(false);
    }
  };

  // Show loading state when no message and no document yet
  // OR when we have a message but no document content yet (agent is still working)
  const hasDocumentContent =
    threadDocument &&
    (threadDocument.typstContent || threadDocument.content || threadDocument.latexContent);

  console.log("[DocumentCanvas] Render state check:", {
    hasMessage: !!message,
    messageStatus: message?.status,
    hasThreadDocument: !!threadDocument,
    hasDocumentContent,
  });

  // Handle stop generation
  const handleStopGeneration = async () => {
    setIsStoppedManually(true);
    setIsCancelling(true);
    try {
      await cancelGeneration({
        workflowId: workflowId || undefined,
        threadId: threadId || threadDocument?.threadId,
        messageId: message?.id || (message as any)?._id,
      });

      toast({
        title: "Generation stopped",
        description: "The document generation has been cancelled.",
        variant: "default",
      });
    } catch (error) {
      console.error("Failed to cancel generation:", error);
      toast({
        title: "Failed to stop generation",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  // --- Status and State Flags ---
  // These are shared between renderHeader and renderContent to ensure UI consistency

  // Use the database document status as the primary source of truth.
  // If a server PDF is already compiled and present in Convex Storage, and the document is not actively retrying,
  // we can treat generation as complete and show the PDF cleanly.
  const isDocumentGenerating =
    (threadDocument?.status === "generating" && !serverPdfUrl) ||
    threadDocument?.isRetrying === true;

  const isDocumentComplete =
    (threadDocument?.status === "completed" ||
      threadDocument?.status === "finalized_ready_for_signature" ||
      (!!serverPdfUrl && !threadDocument?.isRetrying)) &&
    !isDocumentGenerating;

  const isStreamingNow = message?.status === "streaming";
  const isFailed = message?.status === "failed";

  // Check if document is in retry state (workflow is retrying validation)
  const isRetrying = threadDocument?.isRetrying === true;
  const currentAttempt = threadDocument?.currentAttempt || 1;
  const validationFailed = threadDocument?.validationStatus === "invalid" && !isRetrying;
  const hasCompilerError = !!compilerError;

  const isExplicitlyStopped =
    isStoppedManually ||
    isCancelling ||
    threadDocument?.compilationErrors?.includes("Generation stopped by user") ||
    (isFailed && threadDocument?.status === "error");

  const isGenerating =
    (isDocumentGenerating ||
      isRetrying ||
      isStreamingNow ||
      (!isDocumentComplete && !isFailed && !validationFailed && !hasDocumentContent)) &&
    !isExplicitlyStopped;

  const isComplete =
    !isGenerating &&
    (isDocumentComplete || (message?.status === "success" && !isDocumentGenerating));

  // Determine if it was explicitly stopped rather than a system failure
  const isStopped = isExplicitlyStopped;

  const isServiceAgreement = !!threadDocument?.relatedBid && 
    (threadDocument.title.toLowerCase().includes("service agreement") || 
     threadDocument.title.toLowerCase().includes("contract"));

  // Render document header with status and export buttons
  const renderHeader = () => {
    // Only show failed state using the DEBOUNCED flag so transient WASM errors
    // don't flash "Unable to display document" at the user
    const showFailed =
      validationFailed || showCompilerErrorUI || (isFailed && !isRetrying);

    // Check if we're showing the Last Known Good State
    const showLKGSWarning = useLKGS;

    return (
      <div className="mb-6 space-y-4 border-b border-gray-200 pb-4">
        {/* Title and Status Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                "rounded-lg p-2",
                isGenerating
                  ? "bg-purple-100"
                  : isComplete && !showFailed && !showLKGSWarning
                    ? "bg-green-100"
                    : showLKGSWarning
                      ? "bg-yellow-100"
                      : isStopped
                        ? "bg-yellow-100"
                        : "bg-red-100"
              )}
            >
              {isGenerating ? (
                <BarsLoader size={24} className="text-purple-600" />
              ) : isComplete && !showFailed && !showLKGSWarning ? (
                <CheckCircle className="h-6 w-6 text-green-600" />
              ) : showLKGSWarning ? (
                <AlertCircle className="h-6 w-6 text-yellow-600" />
              ) : isStopped ? (
                <Square className="h-6 w-6 fill-yellow-600 text-yellow-600" />
              ) : (
                <AlertCircle className="h-6 w-6 text-red-600" />
              )}
            </div>
            <div>
              {isGenerating ? (
                <h2 className="text-xl font-semibold text-gray-900">
                  {isRetrying ? "Generating Document" : "Generating Document"}
                </h2>
              ) : isStopped ? (
                <h2 className="text-xl font-semibold text-gray-900">Generation Stopped</h2>
              ) : (
                <div className="group flex items-center gap-2">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {threadDocument?.title || "Generated Document"}
                  </h2>
                  {threadDocument?.isTemplate && (
                    <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                      TEMPLATE
                    </span>
                  )}
                  {threadDocument && !isFinalized && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-gray-400 hover:text-gray-600"
                      onClick={() => {
                        setTempTitle(threadDocument.title);
                        setIsEditingTitle(true);
                      }}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              )}
              <p className="text-sm text-gray-500">
                {isGenerating
                  ? "Drafting document..."
                  : isStopped
                    ? "Generation was stopped"
                    : showLKGSWarning
                      ? "Viewing previous version (latest changes failed to compile)"
                      : showFailed
                        ? "Unable to display document"
                        : "Generation complete"}
              </p>
            </div>
          </div>

          {/* Stop button - Only show when generating */}
          {isGenerating && workflowId && (
            <Button
              onClick={handleStopGeneration}
              variant="outline"
              size="sm"
              disabled={isCancelling}
              className="gap-2"
            >
              {isCancelling ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Stopping...
                </>
              ) : (
                <>
                  <Square className="h-4 w-4 fill-red-500 text-red-500" />
                  Stop
                </>
              )}
            </Button>
          )}

          {/* Regenerate Button - Show only when complete or stopped */}
          {(isComplete || isStopped) && threadId && !isFinalized && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={async () => {
                      if (!threadId) return;
                      try {
                        toast({
                          title: "Regenerating...",
                          description: "We are regenerating your document.",
                        });
                        await refineDocument({
                          threadId,
                          prompt:
                            "Please regenerate the entire document using the same context but try to be more concise if it was truncated, or ensure everything is complete.",
                        });
                      } catch (err) {
                        console.error("Failed to regenerate:", err);
                      }
                    }}
                    className="h-8 w-8 text-gray-500 hover:bg-purple-50 hover:text-purple-600"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Regenerate document</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Export Buttons Row - Only show when complete */}
        {(isComplete || isStopped) && !isGenerating && (
          <div className="flex items-center gap-2">
            <Button onClick={handleDownload} variant="outline" size="sm" className="gap-2">
              <Download className="h-4 w-4" />
              Download
            </Button>

            {/* Email Composer Sheet Trigger */}
            {isTypstDocument && (pdfBlob || serverPdfUrl) && userId && (
              <Button
                onClick={handleOpenEmailComposer}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <Mail className="h-4 w-4" />
                Email
              </Button>
            )}

            {/* Save as Template Button - Only show for Typst documents */}
            {isTypstDocument && content && !isFinalized && (
              <Button
                onClick={() => {
                  // Check if user is on free tier
                  const planType = subscriptionData?.organization?.planType;
                  const isFree = !planType || planType.toLowerCase() === "basic";
                  if (isFree) {
                    setShowUpgradePrompt(true);
                  } else {
                    setSaveTemplateDialogOpen(true);
                  }
                }}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <LayoutTemplate className="h-4 w-4" />
                Save Template
              </Button>
            )}

            {/* Save/Update PDF Button - Show for Typst documents with compiled PDF */}
            {isTypstDocument && (pdfBlob || serverPdfUrl) && !isFinalized && (
              <Button
                onClick={handleFinalize}
                variant="default"
                size="sm"
                className="gap-2 bg-blue-600 hover:bg-blue-700"
                disabled={isFinalizing}
              >
                {isFinalizing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {threadDocument?.status === "finalized_ready_for_signature"
                      ? "Updating..."
                      : "Saving..."}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {threadDocument?.status === "finalized_ready_for_signature"
                      ? "Update PDF"
                      : "Save PDF"}
                  </>
                )}
              </Button>
            )}

            {/* Agreement Signing Button - Only show for service agreements from bids */}
            {isTypstDocument && isServiceAgreement && (
              <Button
                onClick={() => navigate(`/documents/${threadDocument._id}/signing-workflow`)}
                variant="outline"
                size="sm"
                className="gap-2 border-purple-200 text-purple-700 hover:bg-purple-50"
              >
                <PenTool className="h-4 w-4" />
                Agreement Signing
              </Button>
            )}

            {/* Show saved status */}
            {threadDocument?.status === "finalized_ready_for_signature" && (
              <div className="flex items-center gap-2 rounded-md bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700">
                <CheckCircle className="h-4 w-4" />
                PDF Saved
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render document content with conditional rendering for Typst vs Markdown
  const renderContent = () => {
    // Use the content variable calculated above which handles both streaming and database content
    const displayContent = content;

    console.log("[DocumentCanvas] renderContent called:", {
      isTypstDocument,
      isStreamingNow,
      isRetrying,
      isGenerating,
      currentAttempt,
      validationFailed,
      hasDisplayContent: !!displayContent,
      displayContentLength: displayContent?.length,
      hasPdfBlob: !!pdfBlob,
      pdfBlobSize: pdfBlob?.size,
      hasCompilerError: !!compilerError,
      hasThreadDocument: !!threadDocument,
    });

    // 0.5 Handle explicitly stopped generation
    if (isExplicitlyStopped) {
      // If we have previous content, we might still want to let it render (LKGS or existing content).
      // But if there is no content to show, or if we want to show a clear stop screen:
      if (!content && !pdfBlob) {
        return (
          <div className="space-y-4">
            <div className="flex flex-col items-center justify-center rounded-lg border border-yellow-200 bg-yellow-50 p-8">
              <OctagonAlert className="mb-4 h-12 w-12 text-yellow-500" />
              <h3 className="mb-2 text-lg font-semibold text-gray-900">Generation Stopped</h3>
              <p className="mb-4 max-w-md text-center text-sm text-gray-600">
                You stopped the document generation. You can regenerate the document or send a new
                request from the sidebar.
              </p>
              {onRefineRequest && (
                <Button onClick={onRefineRequest} className="gap-2 bg-blue-600 hover:bg-blue-700">
                  <FileText className="h-4 w-4" />
                  Regenerate
                </Button>
              )}
            </div>
          </div>
        );
      }
      // If we have content (like LKGS), it will fall through and render it,
      // and the header will already show "Generation Stopped".
    }

    // 0. Handle failed document generation — show friendly message
    if (message?.status === "failed" && !isExplicitlyStopped) {
      console.error(
        "[DocumentCanvas] Message status is failed — suppressing error details from UI"
      );
      return (
        <div className="space-y-4">
          <div className="flex flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-8">
            <AlertCircle className="mb-4 h-12 w-12 text-red-400" />
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              We couldn't generate this document
            </h3>
            <p className="mb-4 max-w-md text-center text-sm text-gray-600">
              Something went wrong while generating your document. Please try again.
            </p>
            {onRefineRequest && (
              <Button onClick={onRefineRequest} className="gap-2 bg-blue-600 hover:bg-blue-700">
                <FileText className="h-4 w-4" />
                Try Again
              </Button>
            )}
          </div>
        </div>
      );
    }

    // 1. Show unified glowing loading state when generating, streaming, or locally compiling
    if (isGenerating || isStreaming || (!pdfBlob && isCompiling)) {
      return (
        <div className="flex min-h-[400px] w-full items-center justify-center px-4 py-12">
          <div className="flex min-h-[400px] w-full max-w-3xl flex-col items-center justify-center rounded-2xl border border-purple-100 bg-white shadow-[0_0_60px_-15px_rgba(147,51,234,0.4)] transition-all duration-1000 ease-in-out">
            <div className="flex animate-pulse flex-col items-center gap-5">
              <div className="relative flex h-20 w-20 items-center justify-center">
                {/* Outer spinning ring */}
                <div className="absolute inset-0 animate-spin rounded-full border-4 border-purple-100 border-t-purple-600"></div>
                {/* Inner document icon */}
                <FileText className="h-8 w-8 text-purple-600" />
              </div>
              <div className="space-y-2 text-center">
                <h3 className="text-xl font-semibold tracking-tight text-gray-800">
                  Drafting document...
                </h3>
                <p className="mx-auto max-w-md text-sm text-gray-500">
                  Gathering context and formatting your professional document.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Show friendly error with Try Again button after final validation failure
    if (validationFailed) {
      return (
        <div className="space-y-4">
          <div className="flex flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-8">
            <AlertCircle className="mb-4 h-12 w-12 text-red-400" />
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              We couldn't generate this document
            </h3>
            <p className="mb-4 max-w-md text-center text-sm text-gray-600">
              We tried our best, but ran into some issues creating your document. Please try again
              or try modifying your request.
            </p>
            {onRefineRequest && (
              <Button onClick={onRefineRequest} className="gap-2 bg-blue-600 hover:bg-blue-700">
                <FileText className="h-4 w-4" />
                Try Again
              </Button>
            )}
          </div>
        </div>
      );
    }

    // For Typst documents, show PDF viewer (after compilation completes)
    if (isTypstDocument && !isStreamingNow) {
      console.log("[DocumentCanvas] Rendering Typst document (not streaming)");

      // Check for persistent compilation error (retries exhausted or unhandled by server)
      // If serverPdfUrl or pdfBlob is available, we have a valid compiled PDF, so ignore any client compilerError!
      const hasPersistentCompilerError =
        !serverPdfUrl && !pdfBlob && !!compilerError && attemptCount >= 2;

      if (hasPersistentCompilerError) {
        // Log the error to console only — do not expose it in the UI
        console.error(
          "[DocumentCanvas] Persistent compilation error (suppressed from UI):",
          compilerError
        );
        return (
          <div className="space-y-4">
            <div className="flex flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-8">
              <AlertCircle className="mb-4 h-12 w-12 text-red-400" />
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                We couldn't compile this document
              </h3>
              <p className="mb-6 max-w-md text-center text-sm text-gray-600">
                There was a persistent syntax error when trying to generate the PDF layout. Please
                tell the AI what to fix in the chat panel, or click below to start over.
              </p>
              <div className="flex gap-4">
                {onRetryRequest && (
                  <Button
                    onClick={onRetryRequest}
                    variant="outline"
                    className="gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Start Over
                  </Button>
                )}
                {onRefineRequest && (
                  <Button onClick={onRefineRequest} className="gap-2 bg-blue-600 hover:bg-blue-700">
                    <MessageSquare className="h-4 w-4" />
                    Open Chat
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      }

      // Log compiler errors to console only (not shown in UI during retry process)
      // The user-friendly error message is shown via validationFailed state after all retries fail
      if (compilerError) {
        console.error("[DocumentCanvas] Compilation error:", compilerError);
      }

      // Compilation errors will be retried automatically (handled above) or fall through to final state

      // 3. Handle empty document state (finished success, but no pdfBlob)
      // Direct regeneration handler
      const handleRegenerateDirectly = async () => {
        if (!threadId) return;

        try {
          setIsRegenerating(true);
          toast({
            title: "Regenerating...",
            description: "We are regenerating your document from scratch.",
          });

          if (onRefineRequest) onRefineRequest(); // Close any open overlays

          await refineDocument({
            threadId,
            prompt:
              "Please regenerate the entire document. It seems something went wrong or the content was truncated last time. Focus on completing the full Typst code.",
          });
        } catch (error) {
          console.error("[DocumentCanvas] Failed to regenerate:", error);
          toast({
            title: "Regeneration failed",
            description: "Could not start regeneration. Please try again from the sidebar.",
            variant: "destructive",
          });
        } finally {
          setIsRegenerating(false);
        }
      };

      // If document content exists in the database but PDF is still being compiled/retrieved,
      // show a clean preparation state instead of an erroneous 'content missing' warning
      if (hasDocumentContent && !pdfBlob && !serverPdfUrl) {
        return (
          <div className="flex min-h-[400px] w-full items-center justify-center px-4 py-12">
            <div className="flex min-h-[400px] w-full max-w-3xl flex-col items-center justify-center rounded-2xl border border-purple-100 bg-white shadow-[0_0_60px_-15px_rgba(147,51,234,0.4)] transition-all duration-1000 ease-in-out">
              <div className="flex animate-pulse flex-col items-center gap-5">
                <div className="relative flex h-20 w-20 items-center justify-center">
                  <div className="absolute inset-0 animate-spin rounded-full border-4 border-purple-100 border-t-purple-600"></div>
                  <FileText className="h-8 w-8 text-purple-600" />
                </div>
                <div className="space-y-2 text-center">
                  <h3 className="text-xl font-semibold tracking-tight text-gray-800">
                    Preparing PDF document...
                  </h3>
                  <p className="mx-auto max-w-md text-sm text-gray-500">
                    Compiling your document and preparing the preview.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      }

      // Handle truly empty document state (finished success, but NO document content was saved at all)
      if (!hasDocumentContent && !pdfBlob && !serverPdfUrl && !isGenerating && !isCompiling && message?.status === "success") {
        return (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 p-12">
            <div className="flex max-w-md flex-col items-center text-center">
              <div className="mb-4 rounded-full bg-amber-100 p-3">
                <AlertCircle className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">Document content missing</h3>
              <p className="mb-6 text-sm text-gray-600">
                The generation process completed, but no document content was saved to our database.
                This can happen if the generation was interrupted or truncated.
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button
                  onClick={handleRegenerateDirectly}
                  className="gap-2 bg-purple-600 shadow-sm hover:bg-purple-700"
                >
                  <RefreshCw className="h-4 w-4" />
                  Regenerate Document
                </Button>
                {onRefineRequest && (
                  <Button
                    variant="outline"
                    onClick={onRefineRequest}
                    className="gap-2 border-purple-200 text-purple-700 hover:bg-purple-50"
                  >
                    <FileText className="h-4 w-4" />
                    Refine Instructions
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      }

      // If document is fully signed, display the merged PDF from storage instead of local compilation
      if (threadDocument?.signedPdfStorageId) {
        return (
          <iframe
            src={`${convexSiteUrl}/getDocument?id=${threadDocument.signedPdfStorageId}&title=${encodeURIComponent(threadDocument.title || "Document")}#view=FitH`}
            className="w-full min-h-[800px] border-0"
            title="Signed Document Viewer"
            onLoad={handlePdfRendered}
          />
        );
      }

      // Unified Service: If server-compiled PDF is available, render it directly with zero client compilation
      if (serverPdfUrl) {
        return (
          <PDFViewer
            key={serverPdfUrl}
            pdfUrl={serverPdfUrl}
            className="w-full"
            onRendered={handlePdfRendered}
          />
        );
      }

      // Fallback: Show locally compiled PDF viewer
      return <PDFViewer pdfBlob={pdfBlob} className="w-full" onRendered={handlePdfRendered} />;
    }

    // Fallback for non-Typst documents or unsupported formats
    if (!isTypstDocument) {
      return (
        <div className="flex flex-col items-center justify-center p-12 text-center">
          <AlertCircle className="mb-4 h-12 w-12 text-gray-400" />
          <p className="mb-2 font-medium text-gray-600">Unsupported document format</p>
          <p className="text-sm text-gray-500">
            This document type ({outputType}) is not supported. Only Typst documents can be
            displayed.
          </p>
        </div>
      );
    }

    // Default fallback - show agent's conversational message as plain text
    // We NEVER show 'displayContent' here for Typst documents because it contains the raw source code
    return (
      <div className="whitespace-pre-line text-gray-700">
        {isTypstDocument ? message?.text || "" : displayContent || ""}
      </div>
    );
  };

  return (
    <>
      <Card className={cn("bg-white shadow-lg", className)}>
        <div className="p-8">
          {renderHeader()}
          <div className="document-content">{renderContent()}</div>
        </div>
      </Card>

      {/* Email Composer Sheet */}
      {isTypstDocument && pdfBlob && userId && threadDocument && (
        <EmailComposerSheet
          isOpen={emailComposerOpen}
          onClose={() => setEmailComposerOpen(false)}
          documentId={threadDocument._id}
          pdfBlob={pdfBlob}
          userId={userId}
          documentTitle={threadDocument.title}
          documentType={threadDocument.outputType}
        />
      )}

      {/* Save as Template Dialog */}
      {isTypstDocument && threadDocument && (
        <SaveTemplateDialog
          isOpen={saveTemplateDialogOpen}
          onClose={() => setSaveTemplateDialogOpen(false)}
          typstContent={threadDocument.typstContent || ""}
          documentTitle={threadDocument.title}
          documentId={threadDocument._id}
        />
      )}

      {/* Upgrade Prompt Dialog for free tier users */}
      <UpgradePromptDialog
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        title="Save Unlimited Templates"
        description="Saving custom templates is a Professional feature. Upgrade to save time and standardize your documents."
        feature="Document Templates"
      />
    </>
  );
}
